import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: pedir código, 2: introducir código + nueva contraseña
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [issuedCode, setIssuedCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.code) {
        setIssuedCode(data.code);
        setCode(data.code);
        toast.success("Código generado");
      } else {
        toast.info(data.message || "Si el correo existe, se generó un código.");
      }
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: newPassword });
      toast.success("Contraseña actualizada. Inicia sesión.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-blanco-off">
      <div className="w-full max-w-md animate-fade-up">
        <img src="/vinote-logo.png" alt="Vinonotas" className="w-24 h-24 object-contain rounded-2xl mb-6" />
        <p className="text-xs uppercase tracking-[0.2em] text-dorado-700 mb-2">Recuperar acceso</p>
        <h2 className="font-serif text-3xl text-negro mb-2">Recuperar contraseña</h2>
        <p className="text-sm text-grises-500 mb-6">
          {step === 1
            ? "Introduce tu correo y te generaremos un código temporal para restablecer tu contraseña."
            : "Introduce el código y tu nueva contraseña."}
        </p>

        {step === 1 ? (
          <form onSubmit={requestCode} className="space-y-5" data-testid="forgot-step1">
            <div>
              <Label className="text-grises-600">Correo electrónico</Label>
              <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" required className="mt-1.5 h-11" />
            </div>
            <Button data-testid="forgot-request-btn" type="submit" disabled={loading} className="w-full h-11 bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full font-medium">
              {loading ? "Generando..." : "Generar código"}
            </Button>
          </form>
        ) : (
          <form onSubmit={resetPassword} className="space-y-5" data-testid="forgot-step2">
            {issuedCode && (
              <div data-testid="forgot-issued-code" className="rounded-xl border border-dorado-500/40 bg-dorado-50 p-4">
                <div className="flex items-center gap-2 text-borgona-900 mb-1">
                  <KeyRound className="w-4 h-4" />
                  <span className="text-sm font-medium">Tu código temporal (válido 1 hora):</span>
                </div>
                <p className="font-mono text-2xl tracking-[0.3em] text-borgona-700 font-bold">{issuedCode}</p>
                <p className="text-xs text-grises-500 mt-1 flex items-start gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  Método provisional: el código se muestra en pantalla porque aún no hay envío por email.
                </p>
              </div>
            )}
            <div>
              <Label className="text-grises-600">Código</Label>
              <Input data-testid="forgot-code-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6 dígitos" required className="mt-1.5 h-11" />
            </div>
            <div>
              <Label className="text-grises-600">Nueva contraseña</Label>
              <PasswordInput testId="forgot-newpassword-input" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Mínimo 6 caracteres" required className="mt-1.5 h-11" />
            </div>
            <Button data-testid="forgot-reset-btn" type="submit" disabled={loading} className="w-full h-11 bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full font-medium">
              {loading ? "Guardando..." : "Cambiar contraseña"}
            </Button>
          </form>
        )}

        <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-1.5 text-sm text-grises-500 hover:text-borgona-700 mt-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
        </Link>
      </div>
    </div>
  );
}
